import { useEffect, useState } from "react";
import { DataTable, DataTableField, DataTableCustomActions } from "../../../../components/data/DataTable";
import { setPage, useEnvironment, useHub } from "../../../../core/Global";
import { InvoiceStatuses } from "../../../../core/Enums";
import Icon from "../../../../components/base/Icon";
import Modal from "../../../../components/base/Modal";
import Indicator from "../../../../components/base/Indicator";
import FlexRow from "../../../../components/base/FlexRow";
import Invoice from "../../../profile/models/Invoice";
import PaymentMethod from "../models/PaymentMethod";
import PaymentCardView from "../components/PaymentCardView";
import filteringProps from "../filtering/invoices.json";
import sortingProps from "../sorting/invoices.json";
import "./InvoicesList.css";


// The customer's own invoices.
//
// This was a plain table that asked for every invoice the account had ever been issued and drew
// all of them. Fine for a month-old account, useless for one three years in -- and there was no
// way to find the one you were looking for. It is a DataTable now: a page at a time, searchable
// by number or by what the invoice was for, and narrowed by status, kind or date.
export default function InvoicesList() {

    const hub = useHub();
    const env = useEnvironment();

    const [selectedItem, selectedItemSet] = useState<Invoice>();
    const [previewOpen, previewOpenSet] = useState<boolean>(false);

    const [payMethod, payMethodSet] = useState<PaymentMethod>();
    const [payMethodFetching, payMethodFetchingSet] = useState<boolean>(false);

    const [refresh, refreshSet] = useState<boolean>(false);

    // The settings layout draws a PageTitleBar and each page fills it, the way the other tables
    // under this layout do.
    useEffect(() => {
        setPage("Invoices", "Finance", "System");
    }, [])

    const downloadUrl = (item?: Invoice) =>
        env.hubUrl + "/base/public/customer/invoice/download/" + item?.id + "/" + item?.downloadToken;


    const NumberCol = (data: Invoice) => (
        <div>
            <div className="invoice-number">{data.invoiceNumber}</div>
            {
                data.paidWith &&
                <div className="invoice-paidwith">{data.paidWith}</div>
            }
        </div>
    )

    // What the invoice was for. The first line is the invoice in almost every case, so the count
    // is only mentioned when it is not.
    const ForCol = (data: Invoice) => (
        <div>
            <div className="invoice-summary">{data.summary || "—"}</div>
            <FlexRow className="invoice-kind">
                {
                    data.kind &&
                    <span className="badge badge-light fs-8 fw-bold me-2">{data.kind}</span>
                }
                {
                    (data.itemsCount ?? 0) > 1 &&
                    <span>+{(data.itemsCount ?? 0) - 1} more</span>
                }
            </FlexRow>
        </div>
    )

    const StatusCol = (data: Invoice) => (
        <div className="fs-7">
            {
                data.invoiceStatus === InvoiceStatuses.Paid ?
                    <span className="badge badge-light-success fw-bold">
                        <Icon icon="circle-check" type="solid" size={10} className="me-1" />
                        Paid
                    </span>
                    : data.invoiceStatus === InvoiceStatuses.Failed ?
                        <span className="badge badge-light-danger fw-bold">
                            <Icon icon="circle-xmark" type="solid" size={10} className="me-1" />
                            Failed
                        </span>
                        :
                        <span className="badge badge-light-warning fw-bold">
                            <Icon icon="clock" type="solid" size={10} className="me-1" />
                            Pending
                        </span>
            }
        </div>
    )

    const TotalCol = (data: Invoice) => (
        <div className="invoice-total">${data.total}</div>
    )

    const DateCol = (data: Invoice) => (
        <div className="fs-7">{data.date}</div>
    )

    // The actions live together in one cell so the row keeps a single right-hand edge whatever
    // state the invoice is in.
    const ActionsCol = (data: Invoice) => (
        <FlexRow justify="end" className="invoice-actions">
            {
                data.invoiceStatus === InvoiceStatuses.Pending &&
                <button type="button" className="btn btn-sm btn-primary"
                    onClick={e => {
                        e.preventDefault();
                        selectedItemSet(data);
                        payMethodFetchingSet(true);
                        window.openModal("modal-invoice-pay");
                        hub.Get("/accounts/profile/paymentmethod/default",
                            res => { payMethodSet(res.data); payMethodFetchingSet(false); },
                            () => payMethodFetchingSet(false));
                    }}>
                    Pay
                </button>
            }
            {
                data.invoiceStatus !== InvoiceStatuses.Failed &&
                <>
                    <button type="button" className="btn btn-sm btn-light btn-active-light-primary"
                        onClick={e => {
                            e.preventDefault();
                            selectedItemSet(data);
                            previewOpenSet(true);
                        }}>
                        View
                    </button>
                    <a href={downloadUrl(data)} target="_blank" rel="noreferrer"
                        className="btn btn-sm btn-light btn-active-light-primary">
                        PDF
                    </a>
                </>
            }
        </FlexRow>
    )


    return (
        <>
            <DataTable
                objectName="Invoice"
                apiRoute="/accounts/profile/invoicelist/"
                filteringProps={filteringProps}
                sortingProps={sortingProps}
                preventCreate
                refresh={refresh}>

                <DataTableField title="Invoice">
                    <NumberCol />
                </DataTableField>

                <DataTableField title="For">
                    <ForCol />
                </DataTableField>

                <DataTableField title="Issued">
                    <DateCol />
                </DataTableField>

                <DataTableField title="Status">
                    <StatusCol />
                </DataTableField>

                <DataTableField title="Total">
                    <TotalCol />
                </DataTableField>

                <DataTableCustomActions>
                    <ActionsCol />
                </DataTableCustomActions>

            </DataTable>

            {
                previewOpen && selectedItem &&
                <Modal id="modal-invoice"
                    title={"Invoice " + selectedItem.invoiceNumber}
                    size="XLarge" fitHeight open visible
                    buttonText="Download PDF"
                    confirmLink={downloadUrl(selectedItem)}
                    onClose={() => { previewOpenSet(false); selectedItemSet(undefined); }}>
                    <div className="invoice-preview">
                        <iframe title="Invoice"
                            src={env.hubUrl + "/base/public/customer/invoice/preview/" + selectedItem.id + "/" + selectedItem.downloadToken} />
                    </div>
                </Modal>
            }

            <Modal id="modal-invoice-pay"
                title="Pay Invoice"
                buttonText="Confirm & Pay"
                confirmDisabled={payMethodFetching || !payMethod}
                onConfirm={callback => {
                    hub.Post("/billings/payments/invoice/pay/" + selectedItem?.id, {},
                        res => {
                            if (res.success) { document.location.href = res.data; return; }
                            callback(true);
                        },
                        () => callback(true));
                }}>
                <div>
                    {payMethodFetching && <Indicator />}
                    {
                        !payMethodFetching && payMethod &&
                        <>
                            <p className="mb-4">
                                Invoice {selectedItem?.invoiceNumber} for ${selectedItem?.total} will be
                                paid with this card:
                            </p>
                            <PaymentCardView item={payMethod} />
                        </>
                    }
                    {
                        !payMethodFetching && !payMethod &&
                        <p className="mb-0">
                            There is no payment method on the account to pay this invoice with. Add
                            one under Payment Methods and come back.
                        </p>
                    }
                </div>
            </Modal>
        </>
    );
}
